import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns'
import { AppDispatch, RootState } from 'store'
import {
  CursorPointer,
  Flex,
  MainHeadline,
  PageFooter,
  PageWrapper,
  Tabs,
  TabsContainer,
} from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { toast } from 'react-toastify'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { FilterEnum, LinkEnum } from 'components/Filter/SingleFilter'
import useQueryPagination from 'hooks/useQueryPagination'
import { getParams, handlePagination } from 'utils/helpers'
import {
  clearInventoryDashboard,
  getInventoryItemsDashboard,
} from 'features/inventory/inventoryItemsSlice'
import {
  clearInventoryStockByLocation,
  createManualInventoryHold,
  getInventoryStockByLocation,
  getInventoryStockValuation,
} from 'features/inventory/inventoryControlSlice'
import { IInventoryItemsDashboardSingleResponse } from 'types/api/inventoryItems.interface'
import { IInvLocalReservation, IInvStockRowVM, IInvStockViewTab } from 'types/inventoryStock.interface'
import { ITEM_NATURE_TO_UI, MOVEMENT_CLASS_META } from '../Shared/inventoryEnumMaps'
import {
  formatInvGhs,
  INV_STOCK_AVAILABLE_CAPTION,
  unitsHintLabel,
} from '../Shared/inventoryFormatters'
import {
  StockAvailableValue,
  StockCellMeta,
  StockCellName,
  StockCellText,
  StockCommitNote,
  StockKpiStrip,
  StockNameLine,
  StockNumText,
  StockOnHandValue,
  StockReservedValue,
} from './InventoryStock.styled'

const STOCK_VIEWS: IInvStockViewTab[] = [
  { id: 'all', label: 'All' },
  { id: 'low', label: 'Low stock' },
  { id: 'critical', label: 'Critical' },
  { id: 'slow', label: 'Slow moving' },
]

const realBelowMin = (item: IInventoryItemsDashboardSingleResponse): boolean =>
  Boolean(item.isLowStock) ||
  (item.quantity != null && item.minLevel != null && item.quantity < item.minLevel)

const lastMovementLabel = (iso: string | null | undefined): string | null => {
  if (!iso) return null
  try {
    return formatDistanceToNowStrict(parseISO(iso), { addSuffix: true })
  } catch {
    return format(new Date(iso), 'd MMM yyyy')
  }
}

const stockSplitLabel = (item: IInventoryItemsDashboardSingleResponse): string | null => {
  const cells = item.stockByLocation
  if (!cells?.length) return null
  return cells.map((cell) => `${cell.location}: ${cell.quantity}`).join(' · ')
}

export const InventoryStock = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { dashboard } = useSelector((store: RootState) => store.inventoryItems)
  const { valuation, stockByLocation } = useSelector((store: RootState) => store.inventoryControl)
  const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
  const [view, setView] = useState<IInvStockViewTab['id']>('all')
  const [reserveRow, setReserveRow] = useState<IInvStockRowVM | null>(null)
  const [reserveQty, setReserveQty] = useState('')
  const [reserveFor, setReserveFor] = useState('')
  const [localReservations, setLocalReservations] = useState<IInvLocalReservation[]>([])

  const queryParams = useMemo(() => {
    const next = { ...params }
    if (view === 'low') next.view = 'low'
    else if (view === 'critical') next.view = 'critical'
    else if (view === 'slow') next.view = 'slow'
    else delete next.view
    return next
  }, [params, view])

  useQueryPagination({
    fetchFunctions: [getInventoryItemsDashboard, getInventoryStockByLocation],
    cleanUpFunctions: [clearInventoryDashboard, clearInventoryStockByLocation],
    params: queryParams,
  })

  useQueryPagination({
    fetchFunctions: [getInventoryStockValuation],
    cleanUpFunctions: [],
    params: {},
  })

  const realItems = dashboard?.data ?? []

  const localReservedFor = (sku: string): number =>
    localReservations.filter((r) => r.sku === sku).reduce((sum, r) => sum + r.qty, 0)

  const handleReserve = async () => {
    if (!reserveRow) return
    const qty = Number(reserveQty)
    if (!qty || qty <= 0) {
      toast.warn('Enter a quantity greater than zero')
      return
    }
    if (reserveRow.available != null && qty > reserveRow.available) {
      toast.warn(`Only ${reserveRow.available} available — reservation cannot exceed it`)
      return
    }
    if (!reserveFor.trim()) {
      toast.warn('Say what the stock is reserved for')
      return
    }
    const resp = await dispatch(
      createManualInventoryHold({
        itemId: reserveRow.id,
        quantity: qty,
        reservedFor: reserveFor.trim(),
        locationId: params.locationId ? Number(params.locationId) : null,
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setLocalReservations((prev) => [
        ...prev,
        { sku: reserveRow.sku, qty, reservedFor: reserveFor.trim() },
      ])
      setReserveRow(null)
      setReserveQty('')
      setReserveFor('')
      dispatch(getInventoryItemsDashboard(queryParams))
    }
  }

  const rows = useMemo<IInvStockRowVM[]>(() => {
    const byLocationMap: { [itemId: number]: typeof stockByLocation.data[0] } = {}
    ;(stockByLocation.data ?? []).forEach((row) => {
      byLocationMap[row.itemId] = row
    })

    return realItems.map((item) => {
      const loc = byLocationMap[item.id]
      const belowMin = realBelowMin(item)
      const reserved = (item.reserved ?? 0) + localReservedFor(item.sku)
      const onHand = loc?.onHand ?? item.quantity
      const available =
        item.available != null
          ? Math.max(item.available - localReservedFor(item.sku), 0)
          : Math.max(onHand - reserved, 0)
      const nature =
        item.nature != null ? ITEM_NATURE_TO_UI[item.nature] ?? null : null
      const movement =
        item.movementClass != null ? MOVEMENT_CLASS_META[item.movementClass] : null
      const valueGhs = item.valueGhs ?? loc?.valueGhs ?? null
      const statusLabel = belowMin
        ? 'BELOW MIN'
        : movement
          ? movement.label
          : 'Ok'
      const statusTone = belowMin ? 'danger' : movement?.tone ?? 'success'

      return {
        id: item.id,
        name: item.name,
        sku: item.sku,
        onHand,
        splitLabel: stockSplitLabel(item) ?? (loc
          ? loc.stockByLocation.map((c) => `${c.location}: ${c.quantity}`).join(' · ')
          : null),
        minLevel: item.minLevel,
        valueLabel: valueGhs != null ? formatInvGhs(valueGhs) : null,
        belowMin,
        critical: Boolean(item.critical),
        statusLabel,
        statusTone,
        lastMovementLabel: lastMovementLabel(item.lastMovementAt),
        natureLabel: nature,
        reserved,
        available,
        reservationsLabel: reserved > 0 ? `${reserved} reserved` : null,
        unitsHint: unitsHintLabel(item.purchaseUnit?.name, item.issueUnit?.name),
      }
    })
  }, [realItems, stockByLocation.data, localReservations])

  const filters = [
    {
      type: FilterEnum.ASYNC_SELECT,
      name: 'Location',
      link: LinkEnum.LOCATIONS,
      propName: 'locationId',
      single: true,
    },
    {
      type: FilterEnum.ASYNC_SELECT,
      link: LinkEnum.INVENTORY_CATEGORIES,
      name: 'Category',
      state: null,
      single: true,
      propName: 'categoryId',
    },
  ]

  const columns: IHonestTableColumn<IInvStockRowVM>[] = [
    {
      id: 'item',
      header: 'ITEM',
      fill: true,
      cell: (row) => (
        <StockCellText>
          <StockNameLine>
            <StockCellName>{row.name}</StockCellName>
            {row.natureLabel && <StatusPill status='neutral'>{row.natureLabel}</StatusPill>}
          </StockNameLine>
          <StockCellMeta>{row.sku}</StockCellMeta>
          {row.unitsHint && <StockCellMeta>{row.unitsHint}</StockCellMeta>}
        </StockCellText>
      ),
    },
    {
      id: 'onHand',
      header: 'ON HAND',
      cell: (row) => (
        <StockCellText>
          <StockOnHandValue $belowMin={row.belowMin}>{row.onHand}</StockOnHandValue>
          {row.splitLabel && <StockCellMeta>{row.splitLabel}</StockCellMeta>}
        </StockCellText>
      ),
    },
    {
      id: 'reserved',
      header: 'RESERVED',
      cell: (row) =>
        row.reserved != null ? (
          <StockReservedValue $active={row.reserved > 0} title={row.reservationsLabel ?? undefined}>
            {row.reserved}
          </StockReservedValue>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    {
      id: 'available',
      header: 'AVAILABLE',
      cell: (row) =>
        row.available != null ? (
          <StockCellText title={row.reservationsLabel ?? undefined}>
            <StockAvailableValue $committed={row.available === 0 && row.onHand > 0}>
              {row.available}
            </StockAvailableValue>
            {row.available === 0 && row.onHand > 0 && (
              <StockCommitNote>fully committed</StockCommitNote>
            )}
          </StockCellText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
    { id: 'min', header: 'MIN', cell: (row) => <StockNumText>{row.minLevel}</StockNumText> },
    {
      id: 'value',
      header: 'VALUE',
      align: 'right',
      cell: (row) =>
        row.valueLabel ? <StockNumText>{row.valueLabel}</StockNumText> : <OtherText grey>—</OtherText>,
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (row) => (
        <Flex center gap='0.5rem'>
          <StatusPill status={row.statusTone} dot={row.statusTone === 'danger'}>
            {row.statusLabel}
          </StatusPill>
          {row.critical && <StatusPill status='accent'>Critical</StatusPill>}
        </Flex>
      ),
    },
    {
      id: 'reserve',
      header: '',
      align: 'right',
      cell: (row) => (
        <Button variant='secondary' size='sm' onClick={() => setReserveRow(row)}>
          Reserve
        </Button>
      ),
    },
    {
      id: 'lastMovement',
      header: 'LAST MOVEMENT',
      cell: (row) =>
        row.lastMovementLabel ? (
          <StockNumText>{row.lastMovementLabel}</StockNumText>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  return (
    <>
      <NavBar
        defaultValue={params.q}
        input
        placeholder='Search stock...'
        setParams={setParams}
        filterOptions={filters}
        enableTemporaryManualClosing
      />
      <PageWrapper>
        <Flex between center mb='16'>
          <Flex column gap='0.25rem'>
            <MainHeadline>Stock on hand</MainHeadline>
            <OtherText grey>
              Quantities and value across stores — the item registry lives under Items.
            </OtherText>
          </Flex>
        </Flex>
        {valuation && (
          <StockKpiStrip>
            <MetricCard
              label='Site stock value'
              value={formatInvGhs(valuation.totalValueGhs)}
            />
            <MetricCard label='SKUs' value={String(valuation.skuCount)} />
            <MetricCard label='Below min' value={String(valuation.belowMinCount)} />
            <MetricCard
              label='Reserved value'
              value={formatInvGhs(valuation.reservedValueGhs)}
              sublabel={`${valuation.reservedCommitments} commitments`}
            />
            <MetricCard label='Slow / obsolete' value={String(valuation.slowObsoleteCount)} />
          </StockKpiStrip>
        )}
        <OtherText grey style={{ marginBottom: '1rem' }}>
          {INV_STOCK_AVAILABLE_CAPTION}
        </OtherText>
        <Flex mb='16'>
          <TabsContainer>
            {STOCK_VIEWS.map((tab) => (
              <Tabs key={tab.id} active={view === tab.id} onClick={() => setView(tab.id)}>
                {tab.label}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        {!rows.length ? (
          <NoItems title='Stock' />
        ) : (
          <HonestTable columns={columns} data={rows} keyField='id' />
        )}
        <PageFooter>
          <Pagination
            setPage={(page) => handlePagination(page, setParams)}
            pageCount={Math.max(dashboard?.totalPages || 0, 1)}
            forcePage={(params.page || 1) - 1}
          />
        </PageFooter>
        {reserveRow && (
          <Modal black>
            <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
              <TitleText weight='bold'>{`Reserve — ${reserveRow.name}`}</TitleText>
              <CursorPointer onClick={() => setReserveRow(null)}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            <Flex column gap='1rem'>
              <Input
                labelText='Quantity'
                type='number'
                value={reserveQty}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReserveQty(e.target.value)}
                nomarg
              />
              <Input
                labelText='Reserved for'
                type='text'
                value={reserveFor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReserveFor(e.target.value)}
                nomarg
              />
              <Button variant='primary' onClick={handleReserve}>
                Hold stock
              </Button>
            </Flex>
          </Modal>
        )}
      </PageWrapper>
    </>
  )
}
